/** @format */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { CoreRef, PaperQualification } from "@question-bank/shared";
import { listQualifications, listSeries, listSubjects, listTiers } from "../api/coreTaxonomy";
import { useBrand } from "../hooks/useBrand";
import { questionImportService } from "../services/questionImportService";
import { Locale } from "../features/i18n/init";
import { notify } from "./Toast/toast";
import { PaperLevelForm, type PaperLevelOption } from "./PaperLevelForm";
import { EMPTY_PAPER_LEVEL, isPaperLevelComplete, type PaperLevelValues } from "./paperLevel";
import type { ImportPdfKeys } from "./PdfImport";

interface PdfImportStartProps {
	keys: ImportPdfKeys;
}

interface CoreItem {
	id: string;
	title: { en: string };
}

/**
 * Collects the paper-level facts for an uploaded set of PDFs, then creates the
 * paper and the PDF import job and starts its extraction pipeline.
 */
export const PdfImportStart = ({ keys }: PdfImportStartProps) => {
	const { brand } = useBrand();
	const navigate = useNavigate();
	const { locale } = useParams<{ locale: Locale }>();
	const [values, setValues] = useState<PaperLevelValues>(EMPTY_PAPER_LEVEL);
	const [subjects, setSubjects] = useState<string[]>([]);
	const [qualifications, setQualifications] = useState<PaperLevelOption[]>([]);
	const [tiers, setTiers] = useState<CoreItem[]>([]);
	const [series, setSeries] = useState<CoreItem[]>([]);
	const [isStarting, setIsStarting] = useState(false);

	// Core is a hard dependency: a failed read leaves its dropdown empty.
	useEffect(() => {
		let isCancelled = false;
		const load = <T,>(request: Promise<{ data: T[] }>, apply: (items: T[]) => void) =>
			request.then((list) => !isCancelled && apply(list.data)).catch(() => !isCancelled && apply([]));
		void load(listSubjects(brand), (items) => setSubjects(items.map((item) => item.title.en)));
		void load(listQualifications(brand), (items) =>
			setQualifications(items.map((item) => ({ value: item.code, label: item.title.en }))),
		);
		void load(listTiers(brand), setTiers);
		void load(listSeries(brand), setSeries);
		return () => {
			isCancelled = true;
		};
	}, [brand]);

	const tierOptions = useMemo(() => tiers.map((item) => ({ value: item.id, label: item.title.en })), [tiers]);
	const seriesOptions = useMemo(() => series.map((item) => ({ value: item.id, label: item.title.en })), [series]);

	const handleStart = async () => {
		const qualification: PaperQualification = {
			code: values.qualification,
			...(qualifications.find((o) => o.value === values.qualification)?.label
				? { title: qualifications.find((o) => o.value === values.qualification)?.label }
				: {}),
		};
		const seasonTitle = seriesOptions.find((o) => o.value === values.season)?.label;
		const season: CoreRef = { code: values.season, ...(seasonTitle ? { title: seasonTitle } : {}) };
		const tierTitle = tierOptions.find((o) => o.value === values.tier)?.label;
		const tier: CoreRef | undefined = values.tier
			? { code: values.tier, ...(tierTitle ? { title: tierTitle } : {}) }
			: undefined;

		setIsStarting(true);
		try {
			const job = await questionImportService.importPdfs(
				brand,
				{
					paperCode: values.paperCode.trim(),
					subject: values.subject.trim(),
					qualification,
					...(tier ? { tier } : {}),
					season,
					year: Number.parseInt(values.year, 10),
					date: values.date,
				},
				keys,
			);
			notify("success", "PDF import started");
			navigate(`/${locale}/admin/import-jobs/${job.id}`);
		} catch (err) {
			notify("error", err instanceof Error ? err.message : "Failed to start PDF import");
		} finally {
			setIsStarting(false);
		}
	};

	return (
		<div className="mt-6">
			<PaperLevelForm
				values={values}
				onChange={setValues}
				subjectOptions={subjects}
				qualificationOptions={qualifications}
				tierOptions={tierOptions}
				seriesOptions={seriesOptions}
				isDisabled={isStarting}
			/>
			<button
				type="button"
				onClick={() => void handleStart()}
				disabled={!isPaperLevelComplete(values) || isStarting}
				className="mt-6 rounded-md bg-(--admin-color-primary-strong) px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
			>
				{isStarting ? "Starting…" : "Start import"}
			</button>
		</div>
	);
};
